import { useEffect, useMemo, useState, useRef } from "react";
import html2pdf from "html2pdf.js";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClinicalService,
  fetchMedicationReview,
  setClinicalServiceName,
} from "../../../../../redux/modules/clinical-services/actions";
import { ClinicalServiceTypes } from "../../../text/data";
import { PatientAcknowledgmentForm } from "../../../../forms/medication-review-forms/PatientAcknowledgmentForm";
import { PersonalMedicationRecordForm } from "../../../../forms/medication-review-forms/PersonalMedicationRecordForm";
import { ProviderNotificationForm } from "../../../../forms/medication-review-forms/ProviderNotificationForm";
import { fetchNotepadByService, fetchNotepad, sendEmailWithPdf, uploadPdf } from "../../../../../redux/modules/patient/actions";
import { displayToast } from "../../../../../redux/modules/toast/actions";
import ConfirmationModalNew from '../../../../common/ConfirmationModalNew';
import { FormContextAMR } from "../../../../forms/form-context";
import { PharmacistsWorksheetForm } from "../../../../forms/medication-review-forms/PharmacistsWorksheetForm";
import { MedicationReviewDocTypes } from "../../service-steps/documentation/data";
import { NotepadFormAMR } from "../../../../forms/medication-review-forms/NotepadFormAMR";
import { filterNotesByServiceDate } from "../../../../../helpers/functions";
import { hasOutstandingInterventionsForService } from "../../../../../helpers/medication-review-pmr-link";
import { ReactSVG } from 'react-svg';
import { getPharmacyInfo } from "../../../../forms/common/getPharmacyInfo";

/**
 * Draw the "Under Review" watermark on every page of a generated jsPDF document.
 * Used by the email/PDF export so multi-page Med Records are watermarked throughout, not
 * just on whichever page the single in-flow overlay happened to land on.
 */
const stampWatermarkOnEveryPage = (pdf, enabled) => {
  if (!enabled || !pdf) return pdf;
  const totalPages = pdf.internal.getNumberOfPages();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    let usedGState = false;
    try {
      if (typeof pdf.setGState === "function" && typeof pdf.GState === "function") {
        pdf.setGState(pdf.GState({ opacity: 0.07 }));
        pdf.setTextColor(0, 0, 0);
        usedGState = true;
      }
    } catch (err) {
      usedGState = false;
    }
    if (!usedGState) pdf.setTextColor(237, 237, 237); // ~7% black on white
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(96);
    pdf.text("Under Review", pageWidth / 2, pageHeight / 2, {
      align: "center",
      baseline: "middle",
      angle: 33,
    });
    try {
      if (usedGState) pdf.setGState(pdf.GState({ opacity: 1 }));
    } catch (err) {
      /* GState not supported on this build — nothing to reset */
    }
  }
  return pdf;
};

/**
 * PMR "Under Review" / "Final Version" state.
 * - Opened from an intervention (`statusKnown`): the PMR reflects the whole medication
 *   review, so it stays "Under Review" while that intervention OR any sibling intervention
 *   on the same review is unresolved (`reviewHasOutstanding`).
 * - Opened elsewhere (med-review docs, medical summary): both flags stay `undefined` so
 *   PersonalMedicationRecordForm derives them from its own medication list.
 * - A finished medication update always counts as resolved / final.
 */
const derivePmrReviewState = (
  completedMedicationUpdate,
  statusKnown,
  interventionIsResolved,
  interventionIsComplete,
  reviewHasOutstanding
) => {
  if (statusKnown) {
    // Never "final" while any intervention on the review is still open, even if this
    // one is done or the review's medication update is already marked complete.
    const hasActive =
      reviewHasOutstanding ||
      (!completedMedicationUpdate && !interventionIsResolved);
    return { hasActive, isFinal: !hasActive && interventionIsComplete };
  }
  if (completedMedicationUpdate) {
    return { hasActive: false, isFinal: true };
  }
  return { hasActive: undefined, isFinal: undefined };
};

const DocumentationForm = ({
  clinicalServiceObj,
  clinicalServiceName,
  medications,
  patient,
  formType,
  interventionStatus,
  /** When opened from an intervention: the id of the medication review it was created
   *  on, so the PMR can stay "Under Review" while a sibling intervention is still open. */
  interventionReviewServiceId = null,
  /** Callers reached through routes without a `:serviceId` (e.g. the intervention
   *  chart) pass the medication review id explicitly so forms stay service-scoped. */
  serviceId: serviceIdFromProps = null,
  onBack = () => {},
 }) => {

  const dispatch = useDispatch();
  const { id: patientId, serviceId: serviceIdFromRoute } = useParams();
  const serviceId = serviceIdFromRoute || serviceIdFromProps || null;
  const { formsNotepad, notepad } = useSelector((state) => state.patients);
  const allInterventions = useSelector(
    (state) => state.intervention?.interventions ?? []
  );
  const location = useLocation();
  const { currentLoggedInUserDetail: userFromCurrent } = useSelector((state) => state.currentLoggedInUserDetail ?? {});
  const { currentLoggedInUserDetail: userFromUsers, pharmacyStores } = useSelector((state) => state.users ?? {});
  const pharmacy = getPharmacyInfo(userFromCurrent ?? userFromUsers, pharmacyStores) ?? {};
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [printMode, setPrintMode] = useState("bw");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const printButtonRef = useRef(null);
  const printableRef = useRef(null);

  const NOTEPAD_FORM_TYPES = new Set([
    MedicationReviewDocTypes.Notepad,
    MedicationReviewDocTypes.NotepadPharmacist,
    MedicationReviewDocTypes.NotepadPatient,
  ]);

  useEffect(() => {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (serviceId && clinicalServiceName) {
      dispatch(fetchNotepadByService(clinicalServiceName, serviceId));
    }
  }, [serviceId, clinicalServiceName, dispatch]);

  const prevNotepadLength = useRef(notepad?.length || 0);
  useEffect(() => {
    if (serviceId && clinicalServiceName) {
      const currentLength = notepad?.length || 0;
      if (currentLength !== prevNotepadLength.current) {
        prevNotepadLength.current = currentLength;
        const timeoutId = window.setTimeout(() => {
          dispatch(fetchNotepadByService(clinicalServiceName, serviceId));
        }, 1000);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [notepad?.length, serviceId, clinicalServiceName, dispatch]);

  useEffect(() => {
    if (!NOTEPAD_FORM_TYPES.has(formType)) return;

    // Avoid duplicate fetch when service-level effect already handles serviceId
    if (!serviceId && patientId) {
      dispatch(fetchNotepad(patientId));
    }
  }, [formType, patientId, serviceId, dispatch]);
  
  
  function handleNextPage() {
    setCurrentPage((prev) => (prev >= totalPages ? prev : prev + 1));
  }

  function handlePreviousPage() {
    setCurrentPage((prev) => (prev <= 1 ? prev : prev - 1));
  }

  let formComponent = <div>Coming Soon!</div>;
  let isLandscape = false;
  let needsBack = false;
  switch (formType) {
    case MedicationReviewDocTypes.PatientAcknowledgement:
      formComponent = <PatientAcknowledgmentForm />;
      break;
    case MedicationReviewDocTypes.ProviderNotification:
      formComponent = <ProviderNotificationForm />;
      break;
    case MedicationReviewDocTypes.PersonalMedRecord:
      formComponent = <PersonalMedicationRecordForm />;
      isLandscape = true;
      break;
    case MedicationReviewDocTypes.PharmacistWorksheet:
      formComponent = <PharmacistsWorksheetForm />;
      break;
    case MedicationReviewDocTypes.StoryBoard:
      formComponent = <PharmacistsWorksheetForm />;
      break;
    case MedicationReviewDocTypes.Notepad:
    case MedicationReviewDocTypes.NotepadPharmacist:
    case MedicationReviewDocTypes.NotepadPatient:
      formComponent = <NotepadFormAMR />;
      break;
    default:
      needsBack = true;
      break;
  }

  useEffect(() => {
    if (needsBack) onBack();
  }, [needsBack, onBack]);

  const assessmentForm =
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm || {};
  const completedMedicationUpdate =
    assessmentForm?.documentation?.completeMedicationUpdate === true;

  // Opened from an intervention: use that intervention's status (+ its siblings) to pick
  // "Under Review" vs "Final Version". Opened anywhere else: leave the flags undefined and
  // let PersonalMedicationRecordForm work it out from its own medication list.
  const normalizedInterventionStatus = String(interventionStatus || "")
    .trim()
    .toLowerCase();
  const hasInterventionStatusContext = normalizedInterventionStatus !== "";
  const interventionIsComplete = normalizedInterventionStatus === "completed";
  const interventionIsResolved =
    interventionIsComplete || normalizedInterventionStatus === "cancelled";

  // Any other intervention on the same medication review that is not yet resolved.
  const reviewHasOutstandingInterventions = useMemo(
    () =>
      hasInterventionStatusContext &&
      hasOutstandingInterventionsForService(
        interventionReviewServiceId,
        allInterventions
      ),
    [hasInterventionStatusContext, interventionReviewServiceId, allInterventions]
  );

  const { hasActive: hasActiveInterventionsForPmr, isFinal: pmrIsFinal } =
    derivePmrReviewState(
      completedMedicationUpdate,
      hasInterventionStatusContext,
      interventionIsResolved,
      interventionIsComplete,
      reviewHasOutstandingInterventions
    );

  function handlePrint() {
    if (!printableRef.current) {
      window.print();
      return;
    }

    // Watermark the print exactly when the on-screen document is watermarked
    // (PersonalMedicationRecordForm renders `.pmr-screen-watermark` when it is "In Review").
    const showPmrWatermark = !!printableRef.current.querySelector(".pmr-screen-watermark");

    const html = printableRef.current.innerHTML;

    const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map((l) => l.outerHTML)
      .join('\n');
    const styles = Array.from(document.querySelectorAll('style'))
      .map((s) => s.outerHTML)
      .join('\n');

    // Put one "Under Review" watermark on every printed page. The print styles in
    // index.scss hide everything outside .print-container and rule out a single
    // fixed overlay, so we drop a watermark layer inside .print-container and repeat
    // the mark down the page at page-height intervals. The on-screen overlay is
    // hidden here so it doesn't show up twice.
    const pageWmPx = Math.round((isLandscape ? 7.5 : 10) * 96); // content height per page @ 0.5in margins
    const watermarkStyle = showPmrWatermark
      ? `.pmr-screen-watermark{display:none !important;}` +
        `#pmr-wm-layer{position:relative;z-index:2147483000;pointer-events:none;}` +
        `#pmr-wm-layer .pmr-wm-mark{position:absolute;left:50%;transform:translate(-50%,-50%) rotate(-33deg);font-family:Inter,Arial,sans-serif;font-weight:500;font-size:150px;line-height:1;white-space:nowrap;color:#000000;opacity:0.07;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}`
      : "";
    const watermarkLayer = showPmrWatermark
      ? `<div id="pmr-wm-layer" aria-hidden="true"></div>`
      : "";
    const watermarkScript = showPmrWatermark
      ? `<script>(function(){var STEP=${pageWmPx};function build(){var layer=document.getElementById('pmr-wm-layer');var host=document.querySelector('.print-container');if(!layer||!host)return;var h=Math.max(host.scrollHeight,document.body.scrollHeight,STEP);var pages=Math.max(1,Math.ceil(h/STEP));layer.textContent='';for(var p=0;p<pages;p++){var m=document.createElement('div');m.className='pmr-wm-mark';m.style.top=Math.min(p*STEP+STEP/2,h-20)+'px';m.textContent='Under Review';layer.appendChild(m);}}window.__pmrBuildWm=build;build();})();</script>`
      : "";

    const pageCss =
      `@page{size: letter ${isLandscape ? "landscape" : "portrait"}; margin: 0.5in;}` +
      `body{margin:0; padding:0; -webkit-print-color-adjust:exact; print-color-adjust:exact;}` +
      `.printable-area{width:${isLandscape ? "10in" : "7.5in"}; margin:0 auto;}` +
      `.printable-area *{max-height:none !important;} ${watermarkStyle}`;
    const printDocHtml =
      `<!doctype html><html><head><meta charset="utf-8"><title>Print</title>` +
      `${links}${styles}<style>${pageCss}</style></head><body>` +
      `<div class="print-container printable-area">${watermarkLayer}${html}</div>` +
      `${watermarkScript}</body></html>`;

    // Blob URL instead of document.write (deprecated); the injected <script> still runs on load.
    const blobUrl = URL.createObjectURL(
      new Blob([printDocHtml], { type: "text/html" })
    );
    const newWin = window.open(blobUrl, "_blank");
    if (!newWin) {
      URL.revokeObjectURL(blobUrl);
      window.print();
      return;
    }
    newWin.focus();
    setTimeout(() => {
      try {
        if (typeof newWin.__pmrBuildWm === "function") newWin.__pmrBuildWm();
      } catch (err) {
        /* watermark rebuild is best-effort */
      }
      try {
        newWin.print();
      } catch (err) {
        console.error('Print failed', err);
      }
      try { newWin.close(); } catch (e) {
        /* window already closed by the user */
      }
      URL.revokeObjectURL(blobUrl);
    }, 500);
  }

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  async function handleSendEmail() {
    const normalizedEmail = (email || "").trim();
    if (!normalizedEmail) {
      setEmailError("Please enter recipient email");
      return;
    }
    if (!isValidEmail(normalizedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (!printableRef.current) {
      setEmailError("Document is not ready to send yet");
      return;
    }

    try {
      setLoading(true);
      setEmailError("");

      const el = printableRef.current;

      // Watermark the PDF exactly when the on-screen document is watermarked.
      const showPmrWatermark = !!el.querySelector(".pmr-screen-watermark");

      // Clone into a fixed off-screen host so html2canvas sees the full form
      // without being constrained by any scroll/overflow/max-height ancestor.
      const clone = el.cloneNode(true);
      // Drop the single in-flow watermark; it is re-stamped on every PDF page below.
      clone
        .querySelectorAll(".pmr-screen-watermark")
        .forEach((node) => node.remove());
      const host = document.createElement("div");
      Object.assign(host.style, {
        position: "absolute",
        top: "-99999px",
        left: "0",
        width: isLandscape ? "1086px" : "816px",
        height: "auto",
        overflow: "visible",
        opacity: "0",
        pointerEvents: "none",
        zIndex: "-1",
      });
      host.appendChild(clone);
      document.body.appendChild(host);

      let pdfBlob;
      try {
        pdfBlob = await html2pdf()
          .set({
            margin: 0,
            filename: `${clinicalServiceName || "document"}.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: { scale: 2, useCORS: true, scrollX: 0, scrollY: 0 },
            jsPDF: {
              unit: "in",
              format: "letter",
              orientation: isLandscape ? "landscape" : "portrait",
            },
          })
          .from(clone)
          .toPdf()
          .get("pdf")
          .then((pdf) => stampWatermarkOnEveryPage(pdf, showPmrWatermark))
          .outputPdf("blob");
      } finally {
        document.body.removeChild(host);
      }

      const pdfFilename = `${clinicalServiceName || "document"}.pdf`;
      const pdfLink = await dispatch(uploadPdf(pdfBlob, pdfFilename));

      const formData = new FormData();
      formData.append("File", pdfBlob, pdfFilename);
      formData.append("ToEmail", normalizedEmail);
      formData.append("Subject", clinicalServiceName || "Document");
      formData.append("PatientName", `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim());
      formData.append("FormName", clinicalServiceName || "Document");
      formData.append("PharmacyName", pharmacy.pharmacyName || "");
      formData.append("StoreNumber", pharmacy.pharmacyStoreNumber || "");
      formData.append("PhoneNumber", pharmacy.pharmacyPhone || "");
      formData.append("FaxNumber", pharmacy.pharmacyFax || "");
      formData.append("StoreEmail", pharmacy.pharmacyEmail || "");
      formData.append("PdfLink", pdfLink);

      await dispatch(sendEmailWithPdf(formData));
      dispatch(displayToast('Email sent successfully', 'success', { autoClose: 4000 }));
      setShowEmailModal(false);
      setEmail("");
    } catch (error) {
      setEmailError(error?.message || "Failed to send email");
    } finally {
      setLoading(false);
    }
  }

  // Memoized so the context value keeps a stable identity between renders.
  const formContextValue = useMemo(() => {
    const notepadSource =
      formsNotepad && formsNotepad.length > 0 ? formsNotepad : notepad || [];
    const serviceDate = new URLSearchParams(location.search).get("serviceDate");
    const form = clinicalServiceObj?.initialAssessment?.initialAssessmentForm || {};
    return {
      formType: formType || "",
      clinicalServiceName: clinicalServiceName || "",
      serviceId: serviceId || null,
      patient: patient || {},
      preAssessment: form.preAssessment || {},
      clinicalAssessment: form.clinicalAssessment || {},
      documentation: form.documentation || {},
      education: form.education || {},
      patientMedications: medications,
      medications: form.medications || {},
      startDateTime: form.startDateTime,
      endDateTime: form.endDateTime,
      hasActiveInterventions: hasActiveInterventionsForPmr,
      pmrIsFinal,
      notepad: filterNotesByServiceDate(notepadSource, serviceDate),
    };
  }, [
    formType,
    clinicalServiceName,
    serviceId,
    patient,
    clinicalServiceObj,
    medications,
    hasActiveInterventionsForPmr,
    pmrIsFinal,
    formsNotepad,
    notepad,
    location.search,
  ]);

  if (!clinicalServiceObj || !clinicalServiceName || !patient) {
    return <div>Loading</div>;
  }

  return (
    <>
    <div className={`p-9 flex flex-row w-full justify-between items-start ${isLandscape ? '' : 'gap-6' }`}>
      <div className={`${isLandscape ? "" : "xl:min-w-[180px]" } print:hidden`}>
        <button onClick={() => onBack()} aria-label="Back" className="flex items-center justify-center">
          <ReactSVG
            src="/icons/back-arrow.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-16 h-16');
            }} 
          />
        </button>         
      </div>
      <div className="relative">
        {currentPage > 1 && (
          <button
            className="absolute -left-[40px] top-1/2"
            onClick={handlePreviousPage}
          >
            <svg
              width="46"
              height="23"
              viewBox="0 0 46 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.1667 20.3333L2 11.1667M2 11.1667L11.1667 2M2"
                stroke="#0B4C5B"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        <div
          className={`print-container ${isLandscape ? "overflow-y-auto overflow-x-auto" : ""} ${isLandscape ? "w-[full6px]" : "w-[8.5in]"} ${isLandscape ? "max-h-[calc(100vh-6rem)] min-h-[8.5in]" : "min-h-[11in]"}`}
        >
          <div className={`${isLandscape ? 'min-h-[8.5in]' : 'min-h-[11in]'}`} ref={printableRef}>
          <FormContextAMR.Provider value={formContextValue}>
            {formComponent}
          </FormContextAMR.Provider>
          </div>
        </div>
        {currentPage < totalPages && (
          <button
            className="absolute -right-[40px] top-1/2"
            onClick={handleNextPage}
          >
            <svg
              width="46"
              height="23"
              viewBox="0 0 46 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              transform="scale(-1, 1)"
            >
              <path
                d="M11.1667 20.3333L2 11.1667M2 11.1667L11.1667 2M2"
                stroke="#0B4C5B"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
      <div className="flex flex-col xl:flex-row bg-white print:hidden">
        <button
          aria-label="Print"
          ref={printButtonRef}
          onClick={() => handlePrint()}
          className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
        >
          <ReactSVG
            src="/icons/printer-outline.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-9 h-9');
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-20 text-gray-800 font-semibold shadow rounded py-2 px-4">Print</span>
        </button>

        <div className="hidden xl:block w-px bg-gray-100 self-center h-9" />
        <button
          aria-label="Email"
          onClick={() => {
            setEmailError("");
            setShowEmailModal(true);
          }}
          className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
        >
          <ReactSVG
            src="/icons/mail-open.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-9 h-9');
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-20 text-gray-800 font-semibold shadow rounded py-2 px-4">Email</span>
        </button>
        <div className="hidden xl:block w-px bg-gray-100 self-center h-9" />
        <button aria-label="Fax" className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20">
          <ReactSVG
            src="/icons/fax-outline.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-9 h-9');
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-[85%] hidden group-hover:block bg-gray-20 text-gray-800 shadow rounded py-2 px-4 whitespace-nowrap">
            <span className="font-semibold">Email Fax</span> - Coming Soon
          </span>
        </button>
      </div>
    </div>
    <ConfirmationModalNew
      open={showEmailModal}
      title={"Send Email"}
      className={"!w-[400px] !max-w-[400px] !p-0"}
      content={() => (
        <div className="p-2">
          <input
            type="email"
            placeholder="Enter recipient email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            className="w-full border border-gray-300 p-2 rounded mb-2"
          />
          {emailError && <p className="text-sm text-red-600 mb-3">{emailError}</p>}
        </div>
      )}
      handleClose={() => { setShowEmailModal(false); setEmailError(""); }}
      onCross={() => { setShowEmailModal(false); setEmailError(""); }}
      confirmText={loading ? "Sending..." : "Send"}
      cancelText={"Cancel"}
      confirmAction={handleSendEmail}
      cancelAction={() => { setShowEmailModal(false); setEmailError(""); }}
      disableConfirm={loading}
      hideCloseIcon={false}
    />
    </>
  );
};

export default DocumentationForm;
